/**
 * Theme state.
 *
 * `preference` is what the user chose (light / dark / follow the system);
 * `resolved` is the concrete theme in effect right now. Keeping them apart
 * means "System" can track the OS while it changes without us losing the
 * user's actual choice. Only the preference is persisted — the resolved
 * value is derived on load.
 */
import { create } from "zustand";

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "kgs.theme";

const media = () => window.matchMedia("(prefers-color-scheme: dark)");

function readPreference(): ThemePreference {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === "light" || stored === "dark" || stored === "system"
    ? stored
    : "system";
}

function resolve(preference: ThemePreference): ResolvedTheme {
  if (preference === "system") return media().matches ? "dark" : "light";
  return preference;
}

/** The DOM is the source of truth MapLibre and CSS both read, so write it here. */
function applyToDom(resolved: ResolvedTheme): void {
  document.documentElement.setAttribute("data-theme", resolved);
}

interface ThemeState {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
  /** Toggles between the two concrete themes, dropping "system". */
  toggle: () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => {
  const preference = readPreference();
  const resolved = resolve(preference);
  applyToDom(resolved);

  // If the user follows the system, track it live: the OS theme can flip
  // (e.g. a sunset schedule) while the page is open.
  media().addEventListener("change", () => {
    if (get().preference !== "system") return;
    const next = resolve("system");
    applyToDom(next);
    set({ resolved: next });
  });

  return {
    preference,
    resolved,
    setPreference: (next) => {
      localStorage.setItem(STORAGE_KEY, next);
      const nextResolved = resolve(next);
      applyToDom(nextResolved);
      set({ preference: next, resolved: nextResolved });
    },
    toggle: () => {
      const next: ThemePreference = get().resolved === "dark" ? "light" : "dark";
      get().setPreference(next);
    },
  };
});
